import React, { Fragment, useMemo } from "react";
import { generateTimeslotKeys, groupSlotKeys, generateGridSlots } from "./utils/slots";
import { DayDateLabel, HourLabel } from "./components/Labels";
import { standardClassNames } from "./utils";
import { startOfWeek, endOfWeek } from "./utils/dates";
import { ClassNames, ItemData, minutes } from "./utils/types";
import { useSelectable } from "./interactivity/selectable";
import { UseInteractivity } from "./interactivity/types";
import { useImmer } from "use-immer";
import { addItem, useItems } from "./utils/items";
import { useHovering } from "./interactivity/hovering";

export const generateSlots = (
  slotSize: minutes,
  start: Date = startOfWeek(),
  end: Date = endOfWeek(),
) => () => {
  const slotKeys = generateTimeslotKeys(start, end, { slotSize });
  const gridSlots = generateGridSlots(slotKeys, "grid");

  const dayGroups = groupSlotKeys(slotKeys, (key) => new Date(key).getDay())
  const timeGroups = groupSlotKeys(slotKeys, (key) => key % (24 * 60 * 60 * 1000));

  const dayGutterSlots = generateGridSlots(
    Object.values(timeGroups)[0], 
    "day-gutter"
  );

  const timeGutterSlots = generateGridSlots(
    Object.values(dayGroups)[0], 
    "time-gutter"
  );

  return { slotKeys, gridSlots, dayGroups, timeGroups, dayGutterSlots, timeGutterSlots }
}

// Need to fake out Tailwind to ensure these are included
const gridColsClassNames = [
  "grid-cols-1",
  "grid-cols-2",
  "grid-cols-3",
  "grid-cols-4",
  "grid-cols-5",
  "grid-cols-6",
  "grid-cols-7",
  "grid-cols-8",
]

export const Week : React.FC<{
  start?: Date;
  end?: Date;
  slotSize?: number;
  items?: ItemData[];
  classNames?: ClassNames;
}> = ({
  start = startOfWeek(),
  end = endOfWeek(start),
  slotSize = 60,
  items = [],
  classNames = standardClassNames
}) => {
  const {
    slotKeys,
    gridSlots,
    dayGroups,
    timeGroups,
    dayGutterSlots,
    timeGutterSlots
  } = useMemo(
    generateSlots(slotSize, start, end),
    [slotSize, start, end]
  );

  const [
    calendarItemsData, 
    setCalendarItemsData
  ] = useImmer<ItemData[]>(items);

  const calendarItems = useItems(
    calendarItemsData, 
    dayGroups, 
    slotSize, 
    'height', 
    classNames.item
  );
  
  const {
    isSelected, 
    callbacks: selectCallbacks
  } = useSelectable({ 
    onSelect: addItem(setCalendarItemsData, slotSize),
  })

  const { callbacks: hoverCallbacks, isHovered } = useHovering({});

  return (
    <div className={"h-full select-none"}>
      <div className={`grid grid-cols-${Object.keys(dayGutterSlots).length + 1}`}>
        <div className={classNames.slot({isGutter: true})}>&nbsp;</div>

        {/* Day Gutter */}
        { Object.values(dayGutterSlots).map(({ key, Slot }) => (
          <Slot 
            key={key} 
            {...hoverCallbacks(key, "day")} 
            className={classNames.slot({isGutter: true, isHovered: isHovered(key, "day")})}
            label={<DayDateLabel time={key} />}
          />
        )) }

        { Object.values(timeGroups).sort().map((timeGroup) => {
          const timeKey = timeGroup[0];

          const GutterSlot = timeGutterSlots[timeKey]?.Slot || (() => <></>);
          const gutterClassName = classNames.slot({
            isGutter: true, 
            isHovered: isHovered(timeKey, "time")
          });

          return <Fragment key={timeKey}>
            <GutterSlot 
              key={timeKey} 
              {...hoverCallbacks(timeKey, "time")} 
              className={gutterClassName}
              label={<HourLabel time={timeKey} />}
            />

            { timeGroup.map((key) => {
              const GridSlot = gridSlots[key]?.Slot || (() => <></>);
              const slotClassName = classNames.slot({
                slotKey: key,
                isHovered: isHovered(key), 
                isSelected: isSelected(key)
              });
              const Items = () => (calendarItems[key] || []).map(({key, itemId, Item}) => <Item key={itemId} />)

              return <GridSlot key={key} className={slotClassName}  {...hoverCallbacks(key)} {...selectCallbacks(key)}>
                <Items />
              </GridSlot>
            })}
          </Fragment>;
        })}

      </div>
    </div>
  );
}

export default Week;